import { useAuth0 } from '@auth0/auth0-react'
import type { ReactNode } from 'react'
import { Loader, Banner, Text } from '@cloudflare/kumo'
import { useHasRole } from './useRoles'
import { useTranslation } from '../i18n'

type Role = 'admin' | 'operator' | 'user'

interface Props {
  children: ReactNode
  roles?: Role[]
}

export function ProtectedRoute({ children, roles }: Props) {
  const { isAuthenticated, isLoading, loginWithRedirect } = useAuth0()
  const matchesRole = useHasRole(...(roles ?? []))
  const hasRole = !roles || matchesRole
  const { t } = useTranslation()

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-kumo-base">
        <Loader size="lg" />
      </div>
    )
  }

  if (!isAuthenticated) {
    void loginWithRedirect()
    return (
      <div className="flex items-center justify-center min-h-screen bg-kumo-base text-kumo-default">
        <Text as="p" variant="secondary" size="sm">
          {t('common.redirecting')}
        </Text>
      </div>
    )
  }

  if (!hasRole) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-kumo-base text-kumo-default p-6">
        <div className="max-w-md w-full">
          <div className="mb-4">
            <Text variant="heading2" as="h1">
              {t('common.accessDenied')}
            </Text>
          </div>
          <Banner variant="alert">{t('common.accessDeniedMessage')}</Banner>
        </div>
      </div>
    )
  }

  return <>{children}</>
}
